import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SoundPlayer from "@/components/SoundPlayer";
import { Search, ArrowRight, Headphones, Zap, Brain, Shield, ChevronDown, ChevronUp, BookOpen, Star, Users, Clock } from "lucide-react";
import { setCanonical } from "@/utils/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [conditions, setConditions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brainwaveInfo, setBrainwaveInfo] = useState({});
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [tip, setTip] = useState(null);
  const [posts, setPosts] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [banner, setBanner] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/conditions`).then(r => {
      setConditions(r.data.conditions);
      setCategories(r.data.categories);
      setBrainwaveInfo(r.data.brainwave_info);
      setSelectedCondition(r.data.conditions[0]);
    }).catch(console.error);
    axios.get(`${API}/tip-of-the-week`).then(r => setTip(r.data.tip)).catch(console.error);
    axios.get(`${API}/blog/posts`).then(r => setPosts(r.data.posts?.slice(0, 3) || [])).catch(console.error);
    axios.get(`${API}/banner/active`).then(r => setBanner(r.data.banner)).catch(console.error);
    axios.get(`${API}/featured-condition`).then(r => setFeatured(r.data.condition)).catch(console.error);
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(console.error);
    document.title = "ToneHealing - Free Sound Therapy & Binaural Beats | 50+ Health Conditions";
    setCanonical("/");
  }, []);

  const filtered = useMemo(() => {
    let list = conditions;
    if (activeCategory !== "All") list = list.filter(c => c.category === activeCategory);
    if (searchTerm) list = list.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return list;
  }, [conditions, activeCategory, searchTerm]);

  const displayedConditions = showAll ? filtered : filtered.slice(0, 12);
  const localStats = JSON.parse(localStorage.getItem("th_stats") || '{"sessions":0,"minutes":0,"streak":0,"lastDate":""}');

  const getStreakMessage = () => {
    if (localStats.sessions === 0) return null;
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (localStats.lastDate === today) return { text: `${localStats.streak} day streak! Keep it going.`, type: "success" };
    if (localStats.lastDate === yesterday) return { text: `${localStats.streak} day streak! Don't break it — start today's session.`, type: "warning" };
    if (localStats.lastDate && localStats.lastDate !== today) return { text: "Don't break your streak! Start a session now to keep your progress.", type: "urgent" };
    return null;
  };
  const streakMsg = getStreakMessage();

  const faqs = [
    { q: "What are binaural beats?", a: "Binaural beats are created when two slightly different frequencies are played in each ear through headphones. Your brain perceives the difference as a rhythmic beat that can entrain your brainwaves to specific therapeutic states like relaxation, focus, or deep sleep." },
    { q: "Do I need headphones?", a: "Yes, headphones are required for binaural beats to work. The technology relies on delivering different frequencies to each ear separately. Any stereo headphones or earbuds will work." },
    { q: "How long should I listen?", a: "We recommend starting with 10-minute sessions and gradually increasing to 20-30 minutes. Consistency matters more than duration — daily short sessions produce better results than occasional long ones." },
    { q: "Are there any side effects?", a: "Sound therapy is generally safe for most people. Some may experience temporary drowsiness or altered states of consciousness. Do not use while driving. Not recommended for epilepsy or seizure disorders." },
    { q: "What are Solfeggio frequencies?", a: "Solfeggio frequencies are ancient wellness tones ranging from 396Hz to 852Hz, each associated with specific wellness properties. For example, 528Hz is known as the 'Love Frequency' for its harmonizing effects." },
    { q: "Is sound therapy scientifically proven?", a: "Research supports that binaural beats can influence brainwave patterns. Studies have shown potential benefits for relaxation, focus, and sleep. While more research is needed, many users report positive experiences. Individual results may vary." },
  ];

  const brainwaveColor = (type) => ({
    Delta: "brainwave-delta",
    Theta: "brainwave-theta",
    Alpha: "brainwave-alpha",
    Beta: "brainwave-beta",
  }[type] || "brainwave-alpha");

  return (
    <div className="relative" data-testid="home-page">
      {/* Hero */}
      <section className="relative pt-24 pb-20 sm:pt-32 sm:pb-28 overflow-hidden" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-bg.png)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/30 to-background" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left: Text */}
            <div>
              <Badge variant="outline" className="mb-4 px-4 py-1.5 border-primary/30 text-primary bg-white/60">
                Free Sound Therapy Platform
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6" data-testid="hero-heading">
                Support Your Mind & Body with
                <span className="text-primary"> Sound Frequencies</span>
                <img src="/hero-meditation.png" alt="Sound therapy meditation" className="inline-block w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover ml-2 align-middle lg:hidden" />
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mb-4 max-w-2xl leading-relaxed">
                Fall asleep in minutes. Melt anxiety away. Sharpen your focus naturally. Free sound therapy designed to support your wellness — no account, no downloads, just press play.
              </p>

              {/* GEO: Definition-lead sentence for AI engine citation */}
              <p className="speakable-intro text-xs text-muted-foreground/60 mb-6 max-w-2xl">
                ToneHealing is a free sound therapy platform that uses binaural beats, Solfeggio frequencies, and ambient soundscapes to naturally support 50+ health conditions — no account required, just press play.
              </p>

              {/* Quick Start Presets */}
              <div className="flex flex-wrap gap-2 mb-6" data-testid="quick-start-presets">
                {[
                  { label: "Sleep Now", slug: "sleep", icon: "🌙" },
                  { label: "Calm Anxiety", slug: "anxiety", icon: "🧘" },
                  { label: "Deep Focus", slug: "focus", icon: "🎯" },
                  { label: "Pain Relief", slug: "pain", icon: "💆" },
                  { label: "Meditate", slug: "meditation", icon: "✨" },
                  { label: "Energy Boost", slug: "energy", icon: "⚡" },
                ].map(preset => (
                  <Link key={preset.slug} to={`/therapy/${preset.slug}`}>
                    <Button variant="outline" size="sm" className="gap-1.5 bg-white/70 hover:bg-primary hover:text-primary-foreground transition-all" data-testid={`quick-${preset.slug}`}>
                      <span className="text-xs">{preset.icon}</span> {preset.label}
                    </Button>
                  </Link>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <a href="#conditions">
                  <Button size="lg" className="gap-2" data-testid="hero-cta">
                    Start Therapy <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
                <a href="#how-it-works">
                  <Button variant="outline" size="lg" className="gap-2 bg-white/60">Learn How It Works</Button>
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
                {stats && <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-primary" /> {stats.total_sessions.toLocaleString()} sessions completed</span>}
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-accent" /> {conditions.length}+ conditions</span>
                {localStats.sessions > 0 && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> Your streak: {localStats.streak} days</span>}
              </div>
            </div>

            {/* Right: Meditation visual with pulsing rings */}
            <div className="hidden lg:flex items-center justify-center relative">
              <div className="absolute w-80 h-80 rounded-full border-2 border-primary/20 hero-ring-1" />
              <div className="absolute w-96 h-96 rounded-full border border-primary/10 hero-ring-2" />
              <div className="absolute w-[28rem] h-[28rem] rounded-full border border-primary/5 hero-ring-3" />
              <img
                src="/hero-meditation.png"
                alt="Person meditating with headphones — sound therapy"
                className="relative z-10 w-72 h-72 object-cover rounded-full drop-shadow-2xl animate-wave-float"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Streak Nudge */}
      {streakMsg && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-4 relative z-10">
          <div className={`rounded-xl px-5 py-3 flex items-center gap-3 ${
            streakMsg.type === "success" ? "bg-green-50 border border-green-200" :
            streakMsg.type === "warning" ? "bg-amber-50 border border-amber-200" :
            "bg-purple-50 border border-purple-200"
          }`} data-testid="streak-nudge">
            <span className="text-lg">{streakMsg.type === "success" ? "🔥" : streakMsg.type === "warning" ? "⚡" : "💜"}</span>
            <p className={`text-sm font-medium ${
              streakMsg.type === "success" ? "text-green-800" :
              streakMsg.type === "warning" ? "text-amber-800" :
              "text-purple-800"
            }`}>{streakMsg.text}</p>
          </div>
        </div>
      )}

      {/* Trust Badges */}
      <section className="py-8" data-testid="trust-badges">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-xs font-semibold tracking-widest text-muted-foreground/60 uppercase mb-4">ToneHealing Featured In</p>
          <img
            src={`${process.env.PUBLIC_URL}/trust-badges.png`}
            alt="ToneHealing featured in FOX, Google News, and Digital Journal"
            className="mx-auto h-10 sm:h-12 object-contain opacity-70"
          />
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">How Sound Therapy Works</h2>
          <p className="text-muted-foreground mb-12 max-w-2xl">Three layers of wellness frequencies work together to guide your brain into therapeutic states.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Headphones, title: "Binaural Beats", desc: "Different frequencies in each ear create brainwave entrainment, guiding your mind into Delta, Theta, Alpha, or Beta states.", color: "text-primary" },
              { icon: Zap, title: "Solfeggio Frequencies", desc: "Ancient wellness tones (396-852 Hz) that resonate at the cellular level, promoting physical relaxation and emotional balance.", color: "text-accent" },
              { icon: Brain, title: "Ambient Soundscapes", desc: "Natural sounds — ocean waves, rain, forest — enhance relaxation, mask distractions, and deepen therapeutic effects.", color: "brainwave-theta" },
            ].map((item, i) => (
              <div key={i} className={`glass rounded-2xl p-8 animate-fade-in-up stagger-${i + 1}`}>
                <item.icon className={`w-8 h-8 ${item.color} mb-4`} />
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {Object.entries(brainwaveInfo).map(([type, info]) => (
              <div key={type} className={`rounded-xl p-4 border ${brainwaveColor(type)}-bg`}>
                <div className="font-mono-freq text-sm font-bold mb-1">{type}</div>
                <div className="font-mono-freq text-xs opacity-70">{info.range}</div>
                <div className="text-xs mt-2 opacity-70">{info.state}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Condition of the Week */}
      {featured && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-4 h-4 text-accent" />
                  <span className="text-xs font-medium text-accent">Featured Therapy of the Week</span>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Sound Therapy for {featured.name}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{featured.description}</p>
              </div>
              <Link to={`/therapy/${featured.slug}`}>
                <Button className="gap-2 shrink-0" data-testid="featured-condition-cta">
                  Try Now <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Condition Browser + Sound Player + Sidebar */}
      <section id="conditions" className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left: Conditions */}
            <div className="lg:col-span-5">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Select Your Health Concern</h2>
              <p className="text-muted-foreground mb-6">50+ conditions with scientifically-tuned frequency therapy.</p>
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search conditions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="condition-search" />
              </div>
              <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
                <button onClick={() => setActiveCategory("All")} className={`category-tag px-3 py-1.5 rounded-full text-xs transition-all ${activeCategory === "All" ? "bg-primary text-primary-foreground active" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>All</button>
                {categories.map(cat => (
                  <button key={cat} onClick={() => setActiveCategory(cat)} className={`category-tag px-3 py-1.5 rounded-full text-xs transition-all ${activeCategory === cat ? "bg-primary text-primary-foreground active" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>{cat}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="condition-grid">
                {displayedConditions.map(c => (
                  <button key={c.slug} onClick={() => setSelectedCondition(c)}
                    className={`condition-card text-left p-3 rounded-xl border transition-all ${selectedCondition?.slug === c.slug ? "bg-primary/5 border-primary/40 shadow-sm" : "bg-card border-border/50 hover:border-primary/30"}`}
                    data-testid={`condition-card-${c.slug}`}>
                    <div className="font-medium text-sm mb-0.5">{c.name}</div>
                    <div className={`font-mono-freq text-xs ${brainwaveColor(c.brainwave_type)}`}>{c.brainwave_type} &middot; {c.frequency}Hz</div>
                  </button>
                ))}
              </div>
              {filtered.length > 12 && (
                <div className="mt-4 text-center">
                  <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)} data-testid="show-all-conditions">
                    {showAll ? "Show Less" : `Show All ${filtered.length}`}
                    {showAll ? <ChevronUp className="ml-1 w-3 h-3" /> : <ChevronDown className="ml-1 w-3 h-3" />}
                  </Button>
                </div>
              )}
              {selectedCondition && (
                <div className="mt-4">
                  <Link to={`/therapy/${selectedCondition.slug}`}>
                    <Button variant="outline" size="sm" className="gap-2" data-testid="view-condition-page">
                      Full therapy page for {selectedCondition.name} <ArrowRight className="w-3 h-3" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Center: Sound Player */}
            <div className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
              <SoundPlayer condition={selectedCondition} />
            </div>

            {/* Right: Sidebar */}
            <div className="lg:col-span-3 space-y-6">
              <div className="ad-sidebar p-3 text-center" data-testid="sidebar-banner">
                {banner && banner.active ? (
                  <a href={banner.click_url || "#"} target="_blank" rel="noopener noreferrer">
                    {banner.image_url ? <img src={banner.image_url} alt={banner.alt_text || "Sponsored"} className="w-full rounded-lg" /> :
                     banner.html_content ? <div dangerouslySetInnerHTML={{ __html: banner.html_content }} className="rounded-lg overflow-hidden" /> :
                     <div className="bg-secondary/50 rounded-lg p-6"><p className="text-xs text-muted-foreground/50">Sponsor Space Available</p></div>}
                    <p className="text-[9px] text-muted-foreground/30 mt-1">Sponsored</p>
                  </a>
                ) : (
                  <div className="bg-secondary/50 rounded-lg p-6 min-h-[200px] flex flex-col items-center justify-center">
                    <p className="text-xs text-muted-foreground/50 font-medium">Your Ad Here</p>
                    <p className="text-[10px] text-muted-foreground/30 mt-1">support@lifeownit.com</p>
                  </div>
                )}
              </div>
              <div className="glass rounded-xl p-4">
                <h4 className="text-sm font-semibold mb-3">Quick Stats</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Your Sessions</span><span className="font-mono-freq font-medium">{localStats.sessions}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Minutes</span><span className="font-mono-freq font-medium">{localStats.minutes}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Day Streak</span><span className="font-mono-freq font-medium">{localStats.streak}</span></div>
                </div>
              </div>
              {tip && (
                <div className="glass rounded-xl p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Shield className="w-3 h-3 text-accent" />
                    <span className="text-[10px] font-medium text-accent">Tip of the Week</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.text}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Safety Warning */}
      <section className="py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
            <h3 className="text-sm font-bold text-destructive mb-2">Important Safety Warning</h3>
            <p className="text-xs text-destructive/80 leading-relaxed">
              DO NOT use sound therapy while driving, operating machinery, or during any activity requiring alertness. These frequencies alter brainwave patterns and may cause drowsiness or altered states. Use only in a safe, stationary environment. Not recommended for epilepsy or seizure disorders.
            </p>
          </div>
        </div>
      </section>


      {/* People Also Ask */}
      <section className="py-16" data-testid="people-also-ask">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-10">People Also Ask About Sound Therapy</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>What is sound therapy and how does it work?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Sound therapy uses specific audio frequencies to influence brainwave patterns through a process called entrainment. When you listen to binaural beats through headphones, slightly different frequencies played in each ear create a perceived rhythmic pulse that guides your brain into therapeutic states — Delta for deep sleep, Theta for meditation, Alpha for relaxation, and Beta for focus. ToneHealing.com offers free access to these frequencies for 56+ health conditions, requiring only headphones and a few minutes of listening time.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Can sound therapy help with anxiety and sleep problems?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Research suggests that binaural beats may support relaxation and improve sleep quality. Alpha waves at 8-10Hz are commonly used to promote calm and reduce anxious feelings, while Delta waves at 0.5-4Hz are associated with deep, restorative sleep. Many users incorporate sound therapy into their daily routine as a complementary wellness tool alongside professional medical care. Individual results may vary, and sound therapy is not a substitute for medical treatment.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Are binaural beats safe for everyone?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Binaural beats are generally considered safe for most adults when used at comfortable volume levels. However, they are not recommended for individuals with epilepsy or seizure disorders, as the rhythmic frequencies could potentially trigger episodes. They should never be used while driving, operating machinery, or during activities requiring full alertness. Children under 12 should use them only with adult supervision. Always consult a healthcare professional if you have concerns about using sound therapy.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>What are Solfeggio frequencies and why are they used in sound therapy?</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">Solfeggio frequencies are a set of ancient tones — 174Hz, 285Hz, 396Hz, 417Hz, 528Hz, 639Hz, 741Hz, 852Hz, and 963Hz — historically used in sacred music and chanting. Each frequency is associated with different wellness properties. For example, 528Hz is known as the "Love Frequency" and is commonly used to support emotional balance, while 396Hz is associated with releasing tension and promoting calm. On ToneHealing.com, Solfeggio frequencies are layered with binaural beats for a multi-dimensional listening experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-10">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="glass rounded-xl overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`faq-item-${i}`}>
                  <span className="text-sm font-medium pr-4">{faq.q}</span>
                  {expandedFaq === i ? <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />}
                </button>
                {expandedFaq === i && <div className="px-5 pb-5"><p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Blog Posts */}
      {posts.length > 0 && (
        <section className="py-20" data-testid="blog-preview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-3xl font-bold">Latest Articles</h2>
              <Link to="/blog"><Button variant="ghost" size="sm" className="gap-1">View All <ArrowRight className="w-3 h-3" /></Button></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map(post => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="glass rounded-2xl p-6 hover:shadow-lg transition-all group">
                  <BookOpen className="w-5 h-5 text-primary mb-3" />
                  <h3 className="font-bold text-base mb-2 group-hover:text-primary transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{post.title}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-3">{post.excerpt?.split('\n').filter(line => !line.startsWith('#') && line.trim() !== post.title?.trim() && line.trim()).join(' ').replace(/\*{1,2}/g, '').trim().slice(0, 200)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom AdSense */}
      <section className="py-8" data-testid="bottom-adsense">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-2xl border border-dashed border-primary/20 bg-white/60 p-8 text-center min-h-[120px] flex flex-col items-center justify-center" data-testid="adsense-bottom">
            <p className="text-sm text-muted-foreground/40">Google AdSense</p>
            <p className="text-xs text-muted-foreground/25 mt-1">Ad space — Pending Google approval</p>
          </div>
          <p className="text-center text-[9px] text-muted-foreground/20 mt-2">Advertisement</p>
        </div>
      </section>

      {/* Schema JSON-LD — Google AI Mode + GEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": "https://tonehealing.com/#organization",
        "name": "ToneHealing",
        "url": "https://tonehealing.com",
        "description": "ToneHealing is a free sound therapy platform using binaural beats, Solfeggio frequencies, and ambient soundscapes for 50+ health conditions.",
        "contactPoint": { "@type": "ContactPoint", "email": "support@lifeownit.com", "contactType": "customer support" }
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://tonehealing.com/#website",
        "name": "ToneHealing",
        "url": "https://tonehealing.com",
        "publisher": { "@id": "https://tonehealing.com/#organization" },
        "potentialAction": { "@type": "SearchAction", "target": "https://tonehealing.com/?q={search_term_string}", "query-input": "required name=search_term_string" }
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "ToneHealing Sound Therapy",
        "description": "Free professional sound therapy using binaural beats and Solfeggio frequencies for 50+ health conditions including anxiety, insomnia, chronic pain, and focus issues.",
        "provider": { "@id": "https://tonehealing.com/#organization" },
        "serviceType": "Sound Therapy",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
        "areaServed": "Worldwide"
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to Use Sound Therapy for Healing",
        "description": "A step-by-step guide to using binaural beats, Solfeggio frequencies, and ambient soundscapes for health and wellness.",
        "step": [
          { "@type": "HowToStep", "name": "Choose Your Condition", "text": "Browse 50+ health conditions including anxiety, insomnia, pain, focus, and more. Select the condition you want to address." },
          { "@type": "HowToStep", "name": "Put On Headphones", "text": "Binaural beats require stereo headphones to work. The technology delivers slightly different frequencies to each ear to create brainwave entrainment." },
          { "@type": "HowToStep", "name": "Customize Your Session", "text": "Set your preferred volume, duration (10-30 minutes), background ambience, and enable Solfeggio frequencies for enhanced wellness." },
          { "@type": "HowToStep", "name": "Relax and Listen", "text": "Press play and relax in a safe, comfortable environment. The frequencies will naturally guide your brainwaves into the therapeutic state for your condition." }
        ]
      })}} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "ToneHealing - Free Sound Therapy & Binaural Beats",
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro"] },
        "url": "https://tonehealing.com"
      })}} />
    </div>
  );
}
